import React, { useState, useEffect } from 'react';
import { BarChart3, Plus, CheckSquare, FileText, Sparkles, Loader2 } from 'lucide-react';
import { esgAPI, exportAPI } from '../../services/api';
import ExportMenu from '../../components/ExportMenu';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TABS = [
  { id: 'metrics',  label: 'ESG Metrics',    icon: BarChart3    },
  { id: 'gri',      label: 'GRI Checklist',  icon: CheckSquare  },
  { id: 'reports',  label: 'Reports',         icon: FileText     },
];

const CAT_COLOR = {
  environmental: 'bg-emerald-100 text-emerald-700',
  social:        'bg-blue-100 text-blue-700',
  governance:    'bg-purple-100 text-purple-700',
};

const GRI_STATUS = {
  not_started:    { cls: 'bg-gray-100 text-gray-500',    label: 'Not Started'    },
  in_progress:    { cls: 'bg-amber-100 text-amber-700',  label: 'In Progress'    },
  complete:       { cls: 'bg-emerald-100 text-emerald-700', label: 'Complete'    },
  not_applicable: { cls: 'bg-gray-50 text-gray-400',     label: 'N/A'            },
};

function Modal({ title, onClose, children, onSubmit, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">{children}</div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={onSubmit} disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin"/>} Save
          </button>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>;
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;
const S = ({ children, ...p }) => <select {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">{children}</select>;

const currentYear = new Date().getFullYear();
const YEARS = Array.from({length:5},(_,i)=>currentYear-i);

export default function ESGPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('super_admin','compliance');

  const [tab, setTab]           = useState('metrics');
  const [metrics, setMetrics]   = useState([]);
  const [gri, setGri]           = useState([]);
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [yearFilter, setYear]   = useState(String(currentYear));
  const [catFilter, setCat]     = useState('');
  const [griSearch, setGriSearch] = useState('');
  const [metricModal, setMetricModal] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportText, setReportText] = useState('');

  const [mForm, setMForm] = useState({ period_year: currentYear, category:'environmental', metric_key:'', metric_label:'', value_numeric:'', unit:'', gri_reference:'', notes:'' });
  const [rForm, setRForm] = useState({ year: currentYear, report_type:'annual', sections:[] });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, g, r] = await Promise.all([esgAPI.metrics({ year:yearFilter, category:catFilter||undefined }), esgAPI.griChecklist(), esgAPI.reports()]);
      setMetrics(m.data.data);
      setGri(g.data.data);
      setReports(r.data.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [yearFilter, catFilter]);

  const saveMetric = async () => {
    setSaving(true);
    try { await esgAPI.addMetric(mForm); toast.success('Metric saved'); setMetricModal(false); loadAll(); }
    catch {} finally { setSaving(false); }
  };

  const updateGRI = async (id, status) => {
    try { await esgAPI.updateGriChecklist(id, { status }); toast.success('GRI item updated'); loadAll(); }
    catch {}
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { data } = await esgAPI.generateReport({ year: parseInt(rForm.year), report_type: rForm.report_type });
      setReportText(data.data.narrative);
      toast.success('ESG report generated');
      setReportModal(false);
      loadAll();
    } catch {} finally { setGenerating(false); }
  };

  // GRI completion summary
  const griComplete = gri.filter(g=>g.status==='complete').length;
  const griTotal    = gri.filter(g=>g.status!=='not_applicable').length;

  // Metrics by category
  const byCategory = metrics.reduce((acc,m) => {
    if (!acc[m.category]) acc[m.category] = [];
    acc[m.category].push(m);
    return acc;
  }, {});

  const filteredGri = griSearch ? gri.filter(g=>g.gri_code.includes(griSearch)||g.title.toLowerCase().includes(griSearch.toLowerCase())) : gri;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">ESG Reporting Engine</h1>
          <p className="text-sm text-gray-500">EU Taxonomy · GRI Standards · Stakeholder Reports</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={yearFilter} onChange={e=>setYear(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none">
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          {canWrite && (
            <button onClick={()=>setReportModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600">
              <Sparkles size={14}/> Generate Report
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[['environmental','Environmental','bg-emerald-50'],['social','Social','bg-blue-50'],['governance','Governance','bg-purple-50']].map(([cat,label,bg])=>(
          <div key={cat} className={clsx('rounded-xl border border-gray-200 p-5',bg)}>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label} Metrics</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{(byCategory[cat]||[]).length}</p>
            <p className="text-xs text-gray-400 mt-0.5">data points in {yearFilter}</p>
          </div>
        ))}
      </div>

      {/* GRI progress */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-700">GRI Completion</p>
          <p className="text-sm font-bold text-gray-900">{griComplete} / {griTotal} disclosures complete</p>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-primary-500 rounded-full transition-all" style={{width:`${griTotal?((griComplete/griTotal)*100):0}%`}}/>
        </div>
        <p className="text-xs text-gray-400 mt-1">{griTotal?((griComplete/griTotal)*100).toFixed(0):0}% complete</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t=>{ const Icon=t.icon; return (
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={clsx('flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab===t.id?'border-primary-500 text-primary-600':'border-transparent text-gray-500 hover:text-gray-700')}>
            <Icon size={14}/>{t.label}
          </button>
        );})}
      </div>

      {/* Metrics tab */}
      {tab==='metrics' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {['','environmental','social','governance'].map(c=>(
                <button key={c} onClick={()=>setCat(c)}
                  className={clsx('px-3 py-1.5 text-xs rounded-lg font-medium capitalize transition-colors',
                    catFilter===c?'bg-primary-500 text-white':'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50')}>
                  {c||'All'}
                </button>
              ))}
            </div>
            {canWrite && <button onClick={()=>setMetricModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={14}/>Add Metric</button>}
          </div>

          {loading ? <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-gray-400"/></div>
          : metrics.length===0 ? <div className="bg-white rounded-xl border border-gray-200 text-center py-12 text-gray-400 text-sm">No metrics recorded for {yearFilter}.</div>
          : Object.entries(byCategory).map(([cat, items]) => (
            <div key={cat} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',CAT_COLOR[cat])}>{cat}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="text-gray-500 text-xs uppercase">
                  <tr>{['Metric','Value','Unit','GRI Ref','Period'].map(h=><th key={h} className="px-4 py-2.5 text-left font-medium">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((m,i)=>(
                    <tr key={m.id} className={i%2===0?'bg-white':'bg-surface'}>
                      <td className="px-4 py-3 font-medium text-gray-900">{m.metric_label}</td>
                      <td className="px-4 py-3 text-gray-700">{m.value_numeric ?? m.value_text ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{m.unit||'—'}</td>
                      <td className="px-4 py-3 text-gray-500">{m.gri_reference||'—'}</td>
                      <td className="px-4 py-3 text-gray-500">{m.period_month ? `${m.period_month}/${m.period_year}` : m.period_year}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* GRI Checklist */}
      {tab==='gri' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <input value={griSearch} onChange={e=>setGriSearch(e.target.value)} placeholder="Search GRI code or title…"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>
            <span className="text-sm text-gray-500">{filteredGri.length} items</span>
          </div>
          {filteredGri.length===0 ? <div className="text-center py-12 text-gray-400 text-sm">No GRI items found.</div>
          : <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
              {filteredGri.map(g=>(
                <div key={g.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-surface transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono font-bold text-gray-500">{g.gri_code}</span>
                      <span className="text-sm font-medium text-gray-900 truncate">{g.title}</span>
                    </div>
                    <p className="text-xs text-gray-400 capitalize">{g.category} · {g.disclosure_type?.replace('_',' ')}</p>
                  </div>
                  {canWrite ? (
                    <select value={g.status} onChange={e=>updateGRI(g.id,e.target.value)}
                      className={clsx('text-xs px-2 py-1 rounded-full font-medium border-0 focus:outline-none cursor-pointer', GRI_STATUS[g.status]?.cls)}>
                      {Object.entries(GRI_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                    </select>
                  ) : (
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium',GRI_STATUS[g.status]?.cls)}>{GRI_STATUS[g.status]?.label}</span>
                  )}
                </div>
              ))}
            </div>}
        </div>
      )}

      {/* Reports */}
      {tab==='reports' && (
        <div className="space-y-4">
          {reportText && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={15} className="text-primary-500"/>
                <h3 className="text-sm font-semibold text-gray-700">Latest Generated Report</h3>
              </div>
              <div className="bg-primary-50 border border-primary-100 rounded-xl p-5 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{reportText}</div>
            </div>
          )}
          {reportText && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Sparkles size={15} className="text-primary-500"/><h3 className="text-sm font-semibold text-gray-700">Latest Generated Report</h3></div>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">AI Generated · Draft</span>
              </div>
              <div className="bg-primary-50 border border-primary-100 rounded-xl p-5 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">{reportText}</div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Version History ({reports.length})</h3>
              <p className="text-xs text-gray-400">All generated ESG reports across all years</p>
            </div>
            {reports.length===0 ? (
              <div className="text-center py-12">
                <FileText size={32} className="mx-auto mb-3 text-gray-300"/>
                <p className="text-sm text-gray-400">No reports generated yet.</p>
                <p className="text-xs text-gray-400 mt-1">Click "Generate Report" above to create your first ESG report.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {reports.map(r=>(
                  <div key={r.id} className="px-5 py-4 hover:bg-surface transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-gray-900">{r.title}</p>
                          {r.ai_generated && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">AI</span>}
                        </div>
                        <p className="text-xs text-gray-400 capitalize">{r.report_type} report · Version {r.version} · {new Date(r.created_at).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'})}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium capitalize',r.status==='published'?'bg-emerald-100 text-emerald-700':r.status==='review'?'bg-amber-100 text-amber-700':'bg-gray-100 text-gray-600')}>{r.status}</span>
                      </div>
                    </div>
                    {r.narrative_text && (
                      <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">{r.narrative_text.slice(0,200)}…</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Metric Modal */}
      {metricModal && <Modal title="Add ESG Metric" onClose={()=>setMetricModal(false)} onSubmit={saveMetric} loading={saving}>
        <div className="grid grid-cols-2 gap-4">
          <F label="Year"><I type="number" value={mForm.period_year} onChange={e=>setMForm(f=>({...f,period_year:e.target.value}))}/></F>
          <F label="Category"><S value={mForm.category} onChange={e=>setMForm(f=>({...f,category:e.target.value}))}>
            <option value="environmental">Environmental</option>
            <option value="social">Social</option>
            <option value="governance">Governance</option>
          </S></F>
          <F label="Metric Key (unique)"><I value={mForm.metric_key} onChange={e=>setMForm(f=>({...f,metric_key:e.target.value}))} placeholder="e.g. energy_consumption_kwh"/></F>
          <F label="Metric Label"><I value={mForm.metric_label} onChange={e=>setMForm(f=>({...f,metric_label:e.target.value}))} placeholder="e.g. Energy Consumption"/></F>
          <F label="Value"><I type="number" step="any" value={mForm.value_numeric} onChange={e=>setMForm(f=>({...f,value_numeric:e.target.value}))}/></F>
          <F label="Unit"><I value={mForm.unit} onChange={e=>setMForm(f=>({...f,unit:e.target.value}))} placeholder="e.g. kWh, %, persons"/></F>
          <F label="GRI Reference"><I value={mForm.gri_reference} onChange={e=>setMForm(f=>({...f,gri_reference:e.target.value}))} placeholder="e.g. GRI 302-1"/></F>
        </div>
      </Modal>}

      {/* Generate Report Modal */}
      {reportModal && <Modal title="Generate ESG Report" onClose={()=>setReportModal(false)} onSubmit={generateReport} loading={generating}>
        <p className="text-sm text-gray-500">AI will generate a full ESG narrative based on all metrics entered for the selected year.</p>
        <div className="grid grid-cols-2 gap-4">
          <F label="Report Year"><S value={rForm.year} onChange={e=>setRForm(f=>({...f,year:e.target.value}))}>
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </S></F>
          <F label="Report Type"><S value={rForm.report_type} onChange={e=>setRForm(f=>({...f,report_type:e.target.value}))}>
            <option value="annual">Annual</option>
            <option value="quarterly">Quarterly</option>
            <option value="stakeholder">Stakeholder</option>
          </S></F>
        </div>
      </Modal>}
    </div>
  );
}
